import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import { Loader } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import { Tournament } from "../../../../gql/graphql";
import { CreationForm } from "../../components/form/CreationForm";
import { PageHeader } from "../../components/PageHeader";

export const UpdateTournament = () => {
  const navigate = useNavigate();
  let tournamentId = useParams().tournamentId;
  const { loading, data } = useQuery<{ tournament: Tournament }>(
    gql`
      query ($id: ID!) {
        tournament(id: $id) {
          id
          title
          description
          startAt
          endAt
          visibleAt
        }
      }
    `,
    { variables: { id: tournamentId }, skip: !tournamentId },
  );
  const [createTournament] = useMutation<{
    createTournament: { id: string };
  }>(gql`
    mutation (
      $title: String!
      $description: String!
      $startAt: DateTime!
      $endAt: DateTime!
    ) {
      createTournament(
        input: {
          title: $title
          description: $description
          startAt: $startAt
          endAt: $endAt
        }
      ) {
        id
      }
    }
  `);
  const [updateTournament] = useMutation<any>(gql`
    mutation (
      $id: ID!
      $title: String!
      $description: String!
      $startAt: DateTime!
      $endAt: DateTime!
      $visibleAt: DateTime
    ) {
      updateTournament(
        id: $id
        input: {
          title: $title
          description: $description
          startAt: $startAt
          endAt: $endAt
          visibleAt: $visibleAt
        }
      ) {
        id
      }
    }
  `);

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="wp-fade-in">
      <PageHeader
        title={tournamentId ? "Edit Tournament" : "Create Tournament"}
        showBackButton={!!tournamentId}
        showBreadcrumbs={false}
      />
      <CreationForm
        title={tournamentId ? "Update tournament" : "Create tournament"}
        schemaDefinition={{
          title: {
            type: "INPUT",
            label: "Title",
            yupConfig: yup.string().required(),
            inputType: "text",
            defaultValue: data?.tournament?.title,
            placeholder: "Enter the tournament title",
          },
          description: {
            type: "RICH_TEXT_EDITOR",
            label: "Description contents",
            yupConfig: yup.string().required(),
            defaultValue: data?.tournament?.description,
          },
          startAt: {
            type: "INPUT",
            label: "Start at",
            yupConfig: yup.date().required(),
            inputType: "date",
            placeholder: "Enter the tournament start date",
            defaultValue: data?.tournament?.startAt,
          },
          endAt: {
            type: "INPUT",
            label: "End at",
            yupConfig: yup.date().required(),
            inputType: "date",
            placeholder: "Enter the tournament end date",
            defaultValue: data?.tournament?.endAt,
          },
          ...(tournamentId && {
            visibleAt: {
              type: "INPUT",
              label: "Visible at",
              yupConfig: yup.date(),
              inputType: "date",
              placeholder:
                "Enter the date when the tournament will be visible to the public",
              defaultValue: data?.tournament?.visibleAt,
            },
          }),
        }}
        onSubmit={async (values) => {
          if (tournamentId) {
            await updateTournament({
              variables: {
                id: tournamentId,
                title: values.title,
                description: values.description,
                startAt: values.startAt,
                endAt: values.endAt,
                visibleAt: values.visibleAt,
              },
            }).then((data) => {
              if (data?.error) {
                throw new Error(data.error.message);
              }
            });
            notifications.show({
              title: "Tournament updated",
              message: "The tournament has been updated",
              color: "green",
              autoClose: 3000,
            });
          } else {
            tournamentId = await createTournament({
              variables: {
                title: values.title,
                description: values.description,
                startAt: values.startAt,
                endAt: values.endAt,
              },
            }).then((data) => {
              if (data?.error) {
                throw new Error(data.error.message);
              }
              return data.data?.createTournament.id;
            });
            navigate("/tournaments/" + tournamentId);
          }
        }}
      />
    </div>
  );
};
